import React from 'react'
import { Table } from 'react-bootstrap'

import { KTIcon } from '../../../../_metronic/helpers'
import { SmartTooltip } from '../../../../components/SmartTooltip/SmartTooltip'
import { TruncatedValue } from '../../../common/TruncatedValue'
import { formatMskDateTime } from '../../matching/routes/Dashboard/core/mskTime'
import {
  CASH_BOX_ATTEMPT_STATE_LABELS,
  CASH_BOX_ATTEMPT_STATE_TONE,
  CashBoxAttempt,
  cashBoxAttemptSourceLabel,
  cashBoxAttemptUserLabel,
} from '../core/cashBoxAttempts.types'

type Props = {
  attempts: CashBoxAttempt[] | null | undefined
}

const time = (raw: string | null) => (raw ? formatMskDateTime(raw) : '-')

/**
 * Cassa attempts of one local-adapter transaction. The backend sends the block only to roles that may
 * see it, so a missing or empty list renders nothing.
 */
export const CashBoxAttempts: React.FC<Props> = ({ attempts }) => {
  if (!attempts || attempts.length === 0) return null

  return (
    <Table bordered striped={false} size='sm' className='mb-0'>
      <thead>
        <tr className='fw-bold text-gray-700'>
          <th>Касса</th>
          <th>Источник</th>
          <th>Состояние</th>
          <th>Причина</th>
          <th>Взял в работу</th>
          <th>Закрыл</th>
          <th>Открыта</th>
          <th>Закрыта</th>
        </tr>
      </thead>
      <tbody>
        {attempts.map((attempt, index) => {
          const tone = CASH_BOX_ATTEMPT_STATE_TONE[attempt.state]
          return (
            <tr key={index} data-testid='cash-box-attempt' className={tone?.row}>
              <td>
                <TruncatedValue value={attempt.cashBox?.title} empty='-' />
              </td>
              <td>{cashBoxAttemptSourceLabel(attempt.source)}</td>
              <td>
                <span className={tone?.badge}>
                  {CASH_BOX_ATTEMPT_STATE_LABELS[attempt.state] ?? attempt.state}
                </span>
              </td>
              <td>
                {attempt.reason?.label ?? '-'}
                {attempt.detail && (
                  <SmartTooltip content={attempt.detail}>
                    <span
                      data-testid='cash-box-attempt-detail'
                      className='ms-2 d-inline-flex'
                      style={{ cursor: 'help' }}
                    >
                      <KTIcon className='fs-2 text-gray-600' iconName='information-5' />
                    </span>
                  </SmartTooltip>
                )}
              </td>
              <td>
                <TruncatedValue value={cashBoxAttemptUserLabel(attempt.takenBy)} empty='-' />
              </td>
              <td>
                {attempt.closedAt ? (
                  <TruncatedValue
                    value={cashBoxAttemptUserLabel(attempt.closedBy)}
                    empty='Система'
                  />
                ) : (
                  '-'
                )}
              </td>
              <td>{time(attempt.openedAt)}</td>
              <td>{time(attempt.closedAt)}</td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

type ToggleProps = { isOpen: boolean; onToggle: () => void }

/** Expander of a local row; shared by every screen that lists adapter attempts. */
export const CashBoxAttemptsToggle: React.FC<ToggleProps> = ({ isOpen, onToggle }) => (
  <button
    type='button'
    className='btn btn-icon btn-sm btn-light-primary'
    aria-expanded={isOpen}
    aria-label='Показать попытки касс'
    onClick={onToggle}
  >
    <KTIcon iconName={isOpen ? 'arrow-down' : 'arrow-right'} className='fs-3' />
  </button>
)
